// Battle Level Reward Table — static reference for the 10 battle
// (PK) levels of a Popularity Battle season: points required per
// level + the two rewards each level unlocks. Thresholds + rewards
// verified in-game; edit LEVEL_REWARDS if the game changes them.
//
// Each reward supports an optional `img` URL (real in-game art can
// be uploaded later); until then a rarity-gradient icon tile stands
// in as the visual.

const ACCENTS = {
  BGMI: "#F88200",
  PUBG: "#258BC8",
};

// Rarity palette matching PUBG Mobile's item rarities: Epic purple,
// Legendary pink/magenta, Mythic red. "pop" is for popularity-granting
// rewards (chicken, heli) — shared gold so they read as one family.
const RARITIES = {
  basic:     { label: "REWARD",     color: "#9aa3b2", grad: "linear-gradient(135deg, #3a4150, #262c38)" },
  epic:      { label: "EPIC",       color: "#c99cf5", grad: "linear-gradient(135deg, #7b3fd4, #3c1e70)" },
  legendary: { label: "LEGENDARY",  color: "#ff6be0", grad: "linear-gradient(135deg, #d433ab, #5f1050)" },
  mythic:    { label: "MYTHIC",     color: "#ff6b6b", grad: "linear-gradient(135deg, #d92e2e, #661010)" },
  pop:       { label: "POPULARITY", color: "#ffd166", grad: "linear-gradient(135deg, #e6ac2e, #7a5408)" },
};

// Reward art lives in public/rewards/ (resized in-game screenshots).
// A null img falls back to the rarity-gradient icon tile.
const IMG_BASE = `${import.meta.env.BASE_URL}rewards/`;

// Level N (index + 1) → points required + the two rewards it unlocks.
const LEVEL_REWARDS = [
  { points: 15,  rewards: [
    { name: "Chicken ×2 (30 pop each)", icon: "🍗", rarity: "pop",     img: "chicken.jpg" },
    { name: "Popularity Coin ×20",    icon: "🪙", rarity: "epic" ,     img: "pop-coins.jpg" },
  ]},
  { points: 50,  rewards: [
    { name: "Invite Window Theme",    icon: "🎨", rarity: "legendary", img: "legendary-info.jpg" },
    { name: "Social Player Card",     icon: "🃏", rarity: "legendary", img: "legendary-info.jpg" },
  ]},
  { points: 120, rewards: [
    { name: "Info Card",              icon: "📇", rarity: "legendary", img: "legendary-info.jpg" },
    { name: "Coins ×80",              icon: "🪙", rarity: "epic" ,     img: "pop-coins.jpg" },
  ]},
  { points: 180, rewards: [
    { name: "Frame",                  icon: "🖼️", rarity: "legendary", img: "frame.jpg" },
    { name: "Avatar",                 icon: "👤", rarity: "legendary", img: "avatar.jpg" },
  ]},
  { points: 220, rewards: [
    { name: "Social Player Card",     icon: "🃏", rarity: "mythic",    img: "social-mythic.jpg" },
    { name: "125K Pop Heli",          icon: "🚁", rarity: "pop",       img: null },
  ]},
  { points: 250, rewards: [
    { name: "Chatroom Background",    icon: "💬", rarity: "mythic",    img: "chatroom.jpg" },
    { name: "Coins ×100",             icon: "🪙", rarity: "epic" ,     img: "pop-coins.jpg" },
  ]},
  { points: 270, rewards: [
    { name: "Info Card",              icon: "📇", rarity: "mythic",    img: "mythic-info.jpg" },
    { name: "Coins ×100",             icon: "🪙", rarity: "epic" ,     img: "pop-coins.jpg" },
  ]},
  { points: 300, rewards: [
    { name: "Partner Pose",           icon: "🕺", rarity: "mythic",    img: "partner.jpg" },
    { name: "Chat Bubble",            icon: "💭", rarity: "mythic",    img: "chat-bubble.jpg" },
  ]},
  { points: 340, rewards: [
    { name: "Lobby Theme",            icon: "🏞️", rarity: "mythic",    img: "lobby.jpg" },
    { name: "Dynamic Nickname",       icon: "✨", rarity: "mythic",    img: "dynamic-name.jpg" },
  ]},
  { points: 370, rewards: [
    { name: "Information Background", icon: "🌌", rarity: "mythic",    img: "background.jpg" },
    { name: "Sticker",                icon: "🏷️", rarity: "mythic",    img: null },
  ]},
];

function RewardTile({ reward }) {
  const r = RARITIES[reward.rarity] || RARITIES.basic;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 9,
        padding: "7px 10px",
        borderRadius: 10,
        background: r.grad,
        border: `1px solid ${r.color}66`,
        boxShadow: `inset 0 0 18px rgba(0,0,0,0.3), 0 0 12px ${r.color}22`,
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {reward.img ? (
        <img
          src={IMG_BASE + reward.img}
          alt={reward.name}
          style={{
            height: 40, width: "auto", maxWidth: 88,
            borderRadius: 8, objectFit: "contain",
            border: `1px solid ${r.color}55`, flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: 34, height: 34, borderRadius: 8, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 17,
            background: r.grad,
            border: `1px solid ${r.color}55`,
            boxShadow: `inset 0 0 12px rgba(0,0,0,0.35), 0 0 10px ${r.color}22`,
          }}
        >
          {reward.icon}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5, fontWeight: 700, color: "#eaeaef",
            letterSpacing: 0.2, whiteSpace: "nowrap",
            overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {reward.name}
        </div>
        <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: 1.6, color: "#c9cdd6", marginTop: 1 }}>
          {r.label}
        </div>
      </div>
    </div>
  );
}

export default function PopBattleLevelChart({ game }) {
  const accent = ACCENTS[game] || "#3498db";
  // Per-game naming convention, same as PopBattleSeason's battleShort:
  // PUBG announces levels as "PK N", BGMI as "Battle N".
  const levelLabel = (n) => (game === "PUBG" ? `PK${n}` : `BATTLE ${n}`);

  return (
    <section style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <span
          style={{
            padding: "6px 12px",
            borderRadius: 999,
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
            color: "#0d1017",
            fontSize: 11.5, fontWeight: 900, letterSpacing: 1.8,
            boxShadow: `0 4px 18px ${accent}66`,
          }}
        >
          🏆 BATTLE LEVEL REWARD TABLE
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: "#a4a7b0" }}>
          {game} · POINTS & REWARDS PER LEVEL
        </span>
      </div>

      <div
        style={{
          borderRadius: 18,
          border: `1px solid ${accent}44`,
          background: `linear-gradient(160deg, ${accent}14 0%, rgba(255,255,255,0.02) 40%, rgba(0,0,0,0) 100%), #0e1218`,
          boxShadow: `0 20px 50px rgba(0,0,0,0.55), 0 0 40px ${accent}22`,
          overflow: "hidden",
          width: "fit-content",
          maxWidth: "100%",
        }}
      >
        <div style={{ padding: "14px 16px 16px", overflowX: "auto" }}>
          {/* Single grid for header + all rows: columns size to their
              actual content (max-content), so the visual gap between
              LEVEL, POINTS, and each REWARDS column is exactly the
              cell padding everywhere — no per-row grids, no slack. */}
          <div
            className="pb-lv-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "max-content max-content max-content max-content",
              alignItems: "center",
            }}
          >
            {(() => {
              const hdr = (extra = {}) => ({
                fontSize: 11.5, fontWeight: 900, letterSpacing: 1.8, color: accent,
                padding: "9px 12px",
                background: `${accent}1C`,
                borderBottom: `1px solid ${accent}55`,
                marginBottom: 6,
                ...extra,
              });
              return (
                <>
                  <span style={hdr({ borderRadius: "10px 0 0 0" })}>LEVEL</span>
                  <span style={hdr({ textAlign: "right" })}>POINTS</span>
                  <span style={hdr({ gridColumn: "3 / 5", borderRadius: "0 10px 0 0" })}>REWARDS</span>
                </>
              );
            })()}

            {LEVEL_REWARDS.map((lvl, i) => {
              const top = i === LEVEL_REWARDS.length - 1;
              const cell = (extra = {}) => ({
                display: "flex", alignItems: "center", height: "100%",
                padding: "9px 12px",
                background: i % 2 ? "rgba(255,255,255,0.025)" : "transparent",
                ...extra,
              });
              return (
                <div key={lvl.points} style={{ display: "contents" }}>
                  <div style={cell({ borderRadius: "10px 0 0 10px" })}>
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
                        fontSize: 15, fontWeight: 700,
                        color: top ? accent : "#fff",
                        textShadow: top ? `0 0 14px ${accent}88` : "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {levelLabel(i + 1)}
                    </span>
                  </div>
                  <div style={cell({ justifyContent: "flex-end" })}>
                    <span
                      style={{
                        fontFamily: "'Rajdhani', system-ui, -apple-system, sans-serif",
                        fontSize: 25, fontWeight: 700, lineHeight: 1,
                        color: top ? accent : "#eaeaef",
                        textShadow: top ? `0 0 16px ${accent}88` : "none",
                        letterSpacing: 1,
                      }}
                    >
                      {lvl.points}
                    </span>
                  </div>
                  <div style={cell()}>
                    <RewardTile reward={lvl.rewards[0]} />
                  </div>
                  <div style={cell({ borderRadius: "0 10px 10px 0" })}>
                    <RewardTile reward={lvl.rewards[1]} />
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ padding: "10px 10px 0", fontSize: 11, color: "#7a7d86", letterSpacing: 0.2 }}>
            Points accumulate from your 1v1 battle settlements across the season;
            each level unlocks both rewards.
          </div>
        </div>
      </div>
    </section>
  );
}
